import ProductsDetailPanel from 'components/DetailPanels/ProductsDetailPanel';
import ShipmentSupplierSubstMixDetailPanel from 'components/DetailPanels/ScenarioEPShipmentSupplierSubstMixDetailPanel';
import BaseTable from 'components/Tables/BaseTable';
import { setContextSearchRM } from 'ducks/ui';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import docDefinition from 'reports/scenariosExposure/scenarioExposureSubstancesScenarios';
import {
  filterDangerousSubstanceWithDirectAndIndirectShipment, filterSubstanceWithDirectAndIndirectShipment,
  groupBy, mixtureSubstancesBySubstance
} from 'selectors/generic';
import { SCENARIO_EP } from 'utils/constants/coreConstants';
import { lcsExternalExposure } from 'utils/constants/lcsTypes';
import tableIcons from 'utils/icons/materialTableIcons';
import columns from 'utils/tables/columns/inventory/substancePolimer';
import noIcon from 'utils/tables/noIcon';
import { currentUser } from 'selectors/currentUser';

pdfMake.addVirtualFileSystem(pdfFonts);

const Component = props => {
  const {
    compositionByProduct, compositionByRawMaterial, contextSearch, lcsById,
    mixtureSubstancesBySubstance, productsById, rawMaterials, rawMaterialsById,
    shipmentsById, scenarios, user
  } = props;
  const { setSearch, setSubstanceId, setScenariosOpen } = props;
  const tableRef = React.useRef(null);

  const [filterCenario, setFilterCenarios] = useState(false);

  const openSubstanceScenarios = id => {
    setSubstanceId(id);
    setScenariosOpen(true);
  };

  const exportPDF = () => {
    const filteredTableData = tableRef.current.state.data;
    pdfMake.createPdf(
      docDefinition(
        filteredTableData,
        lcsById,
        scenarios,
        rawMaterialsById,
        shipmentsById,
        'R - Listagem de Cenários No Produto por Substância'
      ))
      .open();
  };

  let filteredRawMaterials = rawMaterials.filter(rawMaterial => rawMaterial.hazardous);
  if (filterCenario) {
    filteredRawMaterials = rawMaterials
      .filter(rawMaterial => rawMaterial.hazardous)
      .filter(rawMaterial =>
        scenarios.find(s =>
          s.type === SCENARIO_EP &&
          s.substance_id === rawMaterial.id &&
          s.raw_material_supplier_id &&
          lcsExternalExposure.includes(parseInt(s.lcs_id)) &&
          (
            rawMaterial.id === shipmentsById[s.raw_material_supplier_id].raw_material_id ||
            (mixtureSubstancesBySubstance[rawMaterial.id] || [])
              .map(mix => mix.mixture_id)
              .includes(shipmentsById[s.raw_material_supplier_id].raw_material_id)
        )
      )
    );
  }

  return (
    <BaseTable
      ref={tableRef}
      columns={columns(user)}
      data={filteredRawMaterials}
      onRowClick={() => null}
      onRowDoubleClick={rowData => openSubstanceScenarios(rowData.id)}
      onSearchChange={text => setSearch(text)}
      options={{
        searchText: contextSearch
      }}
      actions={[
        {
          isFreeAction: true,
          icon: tableIcons.Pdf,
          tooltip: 'Exportar PDF',
          onClick: exportPDF
        },
        rowData => {
          const substanceId = rowData.id;
          const filteredScenarios = scenarios.find(s =>
            s.type === SCENARIO_EP &&
            s.substance_id === substanceId &&
            s.raw_material_supplier_id &&
            lcsExternalExposure.includes(parseInt(s.lcs_id)) &&
            (
              substanceId === shipmentsById[s.raw_material_supplier_id].raw_material_id ||
              (mixtureSubstancesBySubstance[substanceId] || [])
                .map(mix => mix.mixture_id)
                .includes(shipmentsById[s.raw_material_supplier_id].raw_material_id)
            )
          );
          return {
            icon: filteredScenarios ?
              tableIcons.Scenarios() :
              tableIcons.ScenariosEmpty(),
            tooltip: 'Gerir cenários',
            onClick: (event, rowData) => openSubstanceScenarios(rowData.id)
          };
        },
        {
          isFreeAction: true,
          icon: filterCenario ? tableIcons.FilterWrongStyled : tableIcons.FilterWrong,
          tooltip: 'Filtrar cenários',
          onClick: () => setFilterCenarios(!filterCenario)
        }
      ]}
      detailPanel={[
        // rowData.products was the m2m through the composition rows, one entry
        // per row and so with the same repetitions - which the panel removes
        // with a Set, as it always did.
        rowData => (compositionByRawMaterial[rowData.id] || []).length === 0 ?
          noIcon :
          {
            icon: tableIcons.Products,
            tooltip: 'Produtos',
            render: () => {
              const dangerousProducts = (compositionByRawMaterial[rowData.id] || [])
                .map(composition => composition.product_id)
                .filter( id => {
                  const substanceInProduct = (compositionByProduct[id] || [])
                    .find( mp => mp.raw_material_id === rowData.id);
                  return substanceInProduct && substanceInProduct.contributes_to_hazard;
                });

              return <ProductsDetailPanel rowData={dangerousProducts} data={productsById} title={'Produtos'}/>;
            }
          },
        rowData =>
          ({
            icon: tableIcons.Shipments,
            tooltip: 'Fornecimentos',
            render: () => <ShipmentSupplierSubstMixDetailPanel
              rowData={rowData}
              dataById={rawMaterialsById}
              title={'Fornecimentos da Substância e das Misturas em que participa'}
              extra={{ scenarios, rawMaterialsById, shipmentsById }}
            />
          })
      ]}
    />
  );
};

const makeMapStateToProps = () => {
  const filteredRawMaterials = filterDangerousSubstanceWithDirectAndIndirectShipment();
  // The composition rows read from both sides: by raw material for the list of
  // products a substance is in, by product to find that substance's row inside
  // a product. Built here, once, rather than searched per row.
  const compositionByProduct = groupBy('productRawMaterials', 'product_id');
  const compositionByRawMaterial = groupBy('productRawMaterials', 'raw_material_id');
  const mapStateToProps = (state, props) => ({
    compositionByProduct: compositionByProduct(state),
    compositionByRawMaterial: compositionByRawMaterial(state),
    contextSearch: state.ui.context.searchRM,
    lcsById: state.descriptors.models,
    mixtureSubstancesBySubstance: mixtureSubstancesBySubstance(state),
    products: state.products,
    rawMaterials: filteredRawMaterials(state),
    rawMaterialsById: state.rawMaterials.models,
    productsById: state.products.models,
    suppliersById: state.suppliers.models,
    user: currentUser(state),
    shipmentsById: state.rawMaterialSuppliers.models,
    scenarios: state.scenarios.data
  });
  return mapStateToProps;
};

const mapDispatchToProps = dispatch => ({
  setSearch: search => dispatch(setContextSearchRM(search))
});

export default connect(makeMapStateToProps, mapDispatchToProps)(Component);
